import { useState } from "react";

import { PatentListData } from "@/components/business/PatentContainer/Patent.types";
import ListUnsuitableContainer from "@/components/shared/business/ListUnsuitableContainer/ListUnsuitableContainer";
import { UNSUITABLE_REASON_KEYS } from "@/components/shared/business/UnsuitableReason/constants/unsuitableReason.constants";
import MultipleBulletCell, {
  hasMultipleBulletTooltip,
} from "@/components/shared/ui/MultipleBulletCell/MultipleBulletCell";
import { usePatentListFilterQuery } from "@api/patent/patentList/usePatentListFilter";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import { formatDate } from "@utils/formatDate";
import { handlePatentListError } from "@utils/handleDraftError";

import PatentListStatusContainer from "./components/PatentStatusContainer/PatentStatusContainer";
import PatentTitleContainer from "./components/PatentTitleContainer/PatentTitleContainer";
import { usePatentRowNavigation } from "./hooks/usePatentRowNavigation";
import * as S from "./PatentTableBody.style";
import { EMPTY_SHELL_VALUE } from "../../constants/PatentTable.constants";

type PatentTableRowProps = {
  listItem: PatentListData;
};

const PatentTableRow = ({ listItem }: PatentTableRowProps) => {
  const [isUnsuitableModalOpen, setIsUnsuitableModalOpen] = useState(false);

  const { clickToMove } = usePatentRowNavigation(listItem);

  return (
    <S.PatentListTableTr isError={handlePatentListError(listItem)[0]}>
      <S.PatentListTableId>
        {listItem?.id || EMPTY_SHELL_VALUE}
      </S.PatentListTableId>
      <S.PatentListTablePublicationNumber>
        {listItem?.publicationNumber || EMPTY_SHELL_VALUE}
      </S.PatentListTablePublicationNumber>
      <S.PatentListTablePublicationCountry>
        {listItem?.publicationCountry || EMPTY_SHELL_VALUE}
      </S.PatentListTablePublicationCountry>
      <S.PatentListTableDate>
        {listItem?.publicationDate || EMPTY_SHELL_VALUE}
      </S.PatentListTableDate>
      <S.PatentListTableDate>
        {listItem?.applicationDate || EMPTY_SHELL_VALUE}
      </S.PatentListTableDate>
      <S.PatentListTableApplicant
        $hasTooltip={hasMultipleBulletTooltip(listItem?.applicantName)}
        onClick={clickToMove}
      >
        <MultipleBulletCell value={listItem?.applicantName} />
      </S.PatentListTableApplicant>
      <S.PatentListTableAssignee
        $hasTooltip={hasMultipleBulletTooltip(listItem?.assigneeName)}
        onClick={clickToMove}
      >
        <MultipleBulletCell value={listItem?.assigneeName} />
      </S.PatentListTableAssignee>
      <S.PatentListTableUnsuitable
        isUnsuitable={
          listItem.draftArticleStatusHighest === DRAFT_STATUS_CODE.unsuitable
        }
        isModalOpen={isUnsuitableModalOpen}
      >
        <ListUnsuitableContainer
          onModalStateChange={setIsUnsuitableModalOpen}
          id={Number(listItem.draftArticleId)}
          articleRejectReason={listItem?.articleRejectReason || ""}
          duplArticleId={null}
          draftArticleStatusHighest={listItem.draftArticleStatusHighest}
          mcEditorName={listItem.mcEditorName}
          aiDone={listItem.aiDone ?? false}
          unsuitableReasonUsageType={UNSUITABLE_REASON_KEYS.patentList}
        />
      </S.PatentListTableUnsuitable>
      <PatentTitleContainer
        patentListItem={listItem}
        onClickMove={clickToMove}
      />
      <S.PatentListTableClassification
        status={listItem.draftArticleStatusHighest}
      >
        {listItem?.primaryCategory || EMPTY_SHELL_VALUE}
      </S.PatentListTableClassification>
      <S.PatentListTableStatus
        key={listItem.id}
        code={listItem.draftArticleStatusHighest}
      >
        <PatentListStatusContainer data={listItem} />
      </S.PatentListTableStatus>
      <S.PatentListTableDate>
        {listItem.startDate
          ? formatDate(listItem.startDate, "YY.MM.DD HH:mm:ss")
          : EMPTY_SHELL_VALUE}
      </S.PatentListTableDate>
      <S.PatentListTableDate>
        {listItem.completeDate
          ? formatDate(listItem.completeDate, "YY.MM.DD HH:mm:ss")
          : EMPTY_SHELL_VALUE}
      </S.PatentListTableDate>
      <S.PatentListTableMcEditor>
        {listItem.mcEditorName || EMPTY_SHELL_VALUE}
      </S.PatentListTableMcEditor>
      <S.PatentListTableFtEditor>
        {listItem.ftEditor2Name || EMPTY_SHELL_VALUE}
      </S.PatentListTableFtEditor>
    </S.PatentListTableTr>
  );
};

const PatentTableBody = () => {
  const { data: patentListData } = usePatentListFilterQuery();

  return (
    <S.PatentListTableBody>
      {patentListData?.data.map((listItem) => (
        <PatentTableRow key={`row-${listItem.id}`} listItem={listItem} />
      ))}
    </S.PatentListTableBody>
  );
};

export default PatentTableBody;
